import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { createCheckoutSession, fetchCredits } from '../services/documentApi';
import { setCredits, setIsAdmin } from '../store/authSlice';
import { signOutUser } from '../utils/auth';
import AvatarMenu from './shared/AvatarMenu';
import './GetCredits.css';

const TIERS = [
  {
    sku: 'EMP-APP-1',
    name: 'Starter',
    credits: '1 credit',
    price: '$3.99',
    per: '$3.99 / session',
    desc: 'Perfect for one application.',
    popular: false,
    badge: null,
  },
  {
    sku: 'EMP-APP-3',
    name: 'Value Pack',
    credits: '3 credits',
    price: '$9.99',
    per: '$3.33 / session',
    desc: 'Save 17% per application. Great for applying to a few roles.',
    popular: false,
    badge: null,
  },
  {
    sku: 'EMP-APP-10',
    name: 'Pro Pack',
    credits: '10 credits',
    price: '$29.99',
    per: '$3.00 / session',
    desc: 'Best value for active job seekers. Save 25%.',
    popular: true,
    badge: 'MOST POPULAR',
  },
  {
    sku: 'EMP-ADDON-10',
    name: 'Top-Up',
    credits: '10 credits',
    price: '$24.99',
    per: '$2.50 / session',
    desc: 'Existing customers only. Lowest cost per credit.',
    popular: false,
    badge: 'ADD-ON',
  },
];

export default function GetCredits() {
  const session = useSelector((state) => state.auth.session);
  const user = useSelector((state) => state.auth.user);
  const credits = useSelector((state) => state.auth.credits);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [loadingSku, setLoadingSku] = useState(null);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [showGenerateBanner, setShowGenerateBanner] = useState(
    () => new URLSearchParams(location.search).get('from') === 'generate'
  );

  const handleSignOut = () => signOutUser({ dispatch, navigate, userId: user?.id });

  useEffect(() => {
    if (!showGenerateBanner) return;
    const timer = setTimeout(() => setShowGenerateBanner(false), 10000);
    return () => clearTimeout(timer);
  }, [showGenerateBanner]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('checkout') === 'success') {
      window.history.replaceState({}, '', window.location.pathname);
      setCheckoutSuccess(true);
      if (session?.access_token) {
        fetchCredits(session.access_token).then(data => {
          if (data !== null) {
            dispatch(setCredits(data.credits));
            dispatch(setIsAdmin(data.isAdmin));
          }
        });
      }
    }
  }, [location.search, session, dispatch]);

  const handleBuy = async (sku) => {
    if (loadingSku) return;
    setLoadingSku(sku);
    try {
      const { url } = await createCheckoutSession(sku, session?.access_token);
      window.location.href = url;
    } catch (err) {
      console.error('[GetCredits] checkout error:', err.message);
      setLoadingSku(null);
    }
  };

  const Nav = () => (
    <nav className="gc-nav">
      <div className="gc-nav-brand">
        <span className="gc-nav-logomark">Z</span>
        <span className="gc-nav-wordmark">Zynacular</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button onClick={() => navigate('/')} className="gc-nav-back">
          ← Back to home
        </button>
        {user && (
          <AvatarMenu
            userEmail={user.email}
            credits={credits}
            onSignOut={handleSignOut}
          />
        )}
      </div>
    </nav>
  );

  if (checkoutSuccess) {
    return (
      <div className="gc-page">
        <Nav />
        <div className="gc-success-body">
          <div className="gc-success-wrap">
            <div className="gc-success-icon">✓</div>
            <h2 className="gc-success-heading">Credits added to your account.</h2>
            <p className="gc-success-sub">You're ready to generate your first application package.</p>
            <button onClick={() => navigate('/create')} className="gc-start-btn">
              Start Generating →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gc-page">
      <Nav />
      <div className="gc-body">
        <div className="gc-inner">
          <span className="gc-eyebrow">Pricing</span>
          <h1 className="gc-heading">Get your credits to get started</h1>
          <p className="gc-sub">
            One credit = one complete application package tailored to one job. No subscriptions. No recurring fees. Just pay for what you need.
          </p>

          {showGenerateBanner && (
            <div
              onClick={() => setShowGenerateBanner(false)}
              style={{
                background: 'rgba(45,95,196,0.08)',
                border: '0.5px solid rgba(107,159,255,0.2)',
                borderRadius: '8px',
                padding: '14px 20px',
                marginBottom: '28px',
                fontSize: '13px',
                color: '#8ba3cc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span>
                ✓ Your documents are ready in My Documents. Your form has been saved — it'll be waiting when you return.
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); navigate('/dashboard'); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b9fff',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  whiteSpace: 'nowrap',
                  padding: 0,
                  flexShrink: 0,
                }}
              >
                View My Documents →
              </button>
            </div>
          )}

          <div className="gc-grid">
            {TIERS.map((tier) => (
              <div
                key={tier.sku}
                className={`gc-card${tier.popular ? ' gc-card--popular' : ''}`}
              >
                {tier.badge && (
                  <span className={`gc-badge ${tier.popular ? 'gc-badge--popular' : 'gc-badge--addon'}`}>
                    {tier.badge}
                  </span>
                )}
                <div className="gc-tier-name">{tier.name}</div>
                <div className="gc-tier-credits">{tier.credits}</div>
                <div className="gc-tier-price">{tier.price}</div>
                <div className="gc-tier-per">{tier.per}</div>
                <p className="gc-tier-desc">{tier.desc}</p>
                <button
                  onClick={() => handleBuy(tier.sku)}
                  disabled={loadingSku !== null}
                  className={`gc-buy-btn${tier.popular ? ' gc-buy-btn--filled' : ''}`}
                >
                  {loadingSku === tier.sku ? 'Loading…' : 'Get Started →'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
